// Wrong-project prompt guard — pure, local, zero AI.
//
// The user switches projects all day and eventually types a message meant for
// project B into project A's chat. This module answers one question, offline and
// instantly: "does this opening prompt look like it belongs to a DIFFERENT
// project than the one it is about to be sent to?"
//
// It is deliberately conservative. It only speaks up when a rare, project-
// specific token (a slug, a hostname, a stack name) points somewhere else by a
// wide margin. Anything ambiguous returns null and the prompt sends normally —
// a false alarm is far more annoying than a missed catch.
//
// No React, no DOM, no browser globals: this file is imported by the esbuild
// bundle AND executed straight from source by test/project-match.test.js.

// Words that carry no project signal. English + Hebrew, plus the generic dev
// vocabulary that shows up in every prompt ("fix the bug in the server").
const STOPWORDS = new Set([
  // english
  "the","and","for","with","that","this","from","are","was","were","you","your","our","not","but","all",
  "can","will","would","should","could","have","has","had","its","it's","into","out","own","get","got",
  "please","need","want","make","made","let","lets","also","now","new","old","how","why","what","when",
  "where","who","which","then","than","there","here","them","they","she","her","him","his","one","two",
  "any","some","more","most","less","just","very","too","about","after","before","again","still","only",
  // generic dev vocabulary
  "add","added","fix","fixed","bug","issue","error","errors","code","file","files","line","lines","test",
  "tests","testing","build","builds","run","running","deploy","deployed","server","client","app","apps",
  "page","pages","button","buttons","screen","user","users","data","update","updated","change","changed",
  "create","remove","delete","check","start","stop","restart","commit","branch","merge","push","pull",
  "function","component","module","script","config","settings","feature","support","version","project",
  "api","url","link","log","logs","port","docker","git","npm","node","json","http","https","www","com",
  // hebrew
  "של","את","זה","זו","היא","הוא","אני","אתה","אנחנו","הם","הן","עם","על","אל","כמו","גם","לא","כן",
  "יש","אין","מה","למה","איך","מתי","איפה","כדי","אבל","או","אם","כל","רק","עוד","שוב","הזה","הזו",
  "צריך","צריכה","רוצה","תוכל","בבקשה","תעשה","לעשות","להוסיף","לתקן","באג","שגיאה","קובץ","שורה",
  "כפתור","עמוד","דף","משתמש","נתונים","לשנות","לבדוק","להריץ","שרת","אתר","פרויקט","קוד","מסך",
]);

// Suffixes stripped from URL hostnames so example.co.il contributes "example",
// not "example co il". Longest-first matching happens in hostTokens().
const PUBLIC_SUFFIXES = new Set([
  "com","net","org","io","co","il","dev","app","ai","me","sh","xyz","info","biz","tech","site","online",
  "store","us","uk","de","fr","eu","cloud","link","live","team","run","page","pro","top","space","www",
]);

const HEB = /[\u0590-\u05FF]/;

/** Lowercased, de-punctuated, stopword-free tokens. Hebrew keeps 2-char words. */
export function tokenize(text) {
  const raw = String(text == null ? "" : text).toLowerCase();
  const out = [];
  for (let piece of raw.split(/[^a-z0-9\u0590-\u05FF-]+/)) {
    piece = piece.replace(/^-+|-+$/g, "");
    if (!piece) continue;
    // A hyphenated identifier is a token itself AND contributes its parts, so
    // "poker-face" matches both "poker-face" and a prompt that says "poker".
    const parts = piece.includes("-") ? [piece].concat(piece.split("-")) : [piece];
    for (const tok of parts) {
      if (!tok) continue;
      if (/^\d+$/.test(tok)) continue;            // bare numbers say nothing
      const min = HEB.test(tok) ? 2 : 3;
      if (tok.length < min) continue;
      if (STOPWORDS.has(tok)) continue;
      out.push(tok);
    }
  }
  return out;
}

function hostTokens(url) {
  let host = String(url || "").trim();
  try {
    if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(host)) host = "http://" + host;
    host = new URL(host).hostname;
  } catch (e) {
    host = host.replace(/^[a-z]+:\/\//i, "").split("/")[0];
  }
  const parts = host.toLowerCase().split(".").filter(Boolean);
  const keep = parts.filter((p) => !PUBLIC_SUFFIXES.has(p));
  return keep.join(" ");
}

function projectText(p) {
  if (!p || typeof p !== "object") return "";
  const bits = [
    p.slug, p.nameHe, p.nameEn, p.oneLiner, p.description, p.repo, p.dir,
    p.category, p.hosting, p.location,
    Array.isArray(p.stack) ? p.stack.join(" ") : "",
    Array.isArray(p.integrations) ? p.integrations.join(" ") : "",
    p.db && p.db.type ? p.db.type : "",
    p.db && p.db.host ? p.db.host : "",
  ];
  const urls = Array.isArray(p.urls) ? p.urls : [];
  for (const u of urls) bits.push(hostTokens(typeof u === "string" ? u : (u && (u.url || u.href)) || ""));
  return bits.filter(Boolean).join(" ");
}

/**
 * Per-project token sets plus corpus-wide IDF weights.
 * Tokens present in EVERY project are dropped outright — they can never
 * distinguish one project from another.
 */
export function buildProfiles(projects) {
  const list = (Array.isArray(projects) ? projects : []).filter((p) => p && p.slug);
  const N = list.length;
  const entries = list.map((p) => ({
    slug: String(p.slug),
    name: p.nameHe || p.nameEn || p.slug,
    tokens: new Set(tokenize(projectText(p))),
  }));
  const df = new Map();
  for (const e of entries) for (const tok of e.tokens) df.set(tok, (df.get(tok) || 0) + 1);
  const idf = new Map();
  for (const [tok, n] of df) {
    if (N > 1 && n >= N) continue;              // in everything → zero signal
    idf.set(tok, Math.log(N / n));
  }
  for (const e of entries) {
    for (const tok of Array.from(e.tokens)) if (!idf.has(tok)) e.tokens.delete(tok);
    e.size = Math.max(1, e.tokens.size);
  }
  return { N, projects: entries, idf, df };
}

// A token is "strong" when at most two projects in the whole hub mention it —
// a slug, a hostname, a one-off library. Those are what make a match credible.
export const STRONG_DF = 2;
// Minimum normalized score before we dare interrupt a send.
export const MIN_SCORE = 0.15;
export const IGNORE_KEY = "hub.promptGuardIgnore";

export function ignorePairKey(fromSlug, toSlug) { return String(fromSlug) + ">" + String(toSlug); }

function scoreProject(entry, promptTokens, profiles) {
  let score = 0;
  const hits = [];
  for (const tok of promptTokens) {
    if (!entry.tokens.has(tok)) continue;
    const w = profiles.idf.get(tok) || 0;
    if (w <= 0) continue;
    score += w;
    hits.push(tok);
  }
  return { score: score / Math.sqrt(entry.size), hits };
}

/**
 * @returns {{slug:string,name:string,score:number,tokens:string[]}|null}
 *   null whenever the evidence is anything short of decisive.
 */
export function matchProject(prompt, profiles, currentSlug, opts) {
  opts = opts || {};
  if (!profiles || !profiles.projects || profiles.projects.length < 2) return null;
  const promptTokens = Array.from(new Set(tokenize(prompt)));
  if (promptTokens.length < 2) return null;      // too little to judge

  const ignore = new Set(Array.isArray(opts.ignore) ? opts.ignore : []);
  const scored = profiles.projects.map((e) => Object.assign({ entry: e }, scoreProject(e, promptTokens, profiles)));
  const current = scored.find((s) => s.entry.slug === currentSlug);
  const currentScore = current ? current.score : 0;

  const others = scored.filter((s) => s.entry.slug !== currentSlug).sort((a, b) => b.score - a.score);
  const top = others[0];
  if (!top || top.score <= 0) return null;
  // The top project overall must be the OTHER one — if the current project ties
  // or wins, the prompt is where it belongs.
  if (top.score <= currentScore) return null;
  if (ignore.has(ignorePairKey(currentSlug, top.entry.slug))) return null;
  // Second place must not be nearly as good, or we are just guessing.
  if (others[1] && others[1].score >= top.score * 0.9) return null;

  const strong = top.hits.filter((tok) => (profiles.df.get(tok) || 0) <= STRONG_DF && (profiles.idf.get(tok) || 0) > 0);
  if (!strong.length) return null;
  if (top.score < MIN_SCORE) return null;
  if (top.score < currentScore * 2 + MIN_SCORE) return null;

  return { slug: top.entry.slug, name: top.entry.name, score: top.score, tokens: strong.concat(top.hits.filter((t) => strong.indexOf(t) < 0)).slice(0, 6) };
}

/**
 * The whole guard decision in one call, so the UI (and its test) never has to
 * re-derive the rules. Returns null unless the send should be interrupted.
 */
export function guardPrompt({ prompt, isFirstTurn, projects, currentSlug, ignore }) {
  if (!isFirstTurn) return null;
  return matchProject(prompt, buildProfiles(projects), currentSlug, { ignore });
}
